import * as mutationTypes from '@/store/mutation-types'
import * as EsignAgreementsApi from '@/api/esign-bridge'
import { ESIGN_ERRORS } from '@/shared/constants/esign-bridge/errors'

// One form version's After Submit → Agreements setup. The module is global, so every load starts clean.
const emptyState = () => ({
  agreements: [],
  esignAvailable: true,
  loaded: false,
  loadFailed: false,
  // "formId:variantId" the state belongs to. The tab content is rebuilt on every visit, so a
  // version already loaded is not fetched (or checked with CliqSign) again.
  loadedFor: null
})

const state = emptyState()

const getters = {
  getAgreements: state => state.agreements,
  isEsignAvailable: state => state.esignAvailable,
  isLoaded: state => state.loaded,
  hasLoadFailed: state => state.loadFailed
}

const isPlanError = error => error?.response?.data?.meta?.error_type === ESIGN_ERRORS.NOT_AVAILABLE

const actions = {
  async loadSetup({ commit, state }, { formId, variantId, force = false }) {
    const key = `${formId}:${variantId}`
    if (!force && state.loadedFor === key && state.loaded && !state.loadFailed) {
      return
    }

    commit(mutationTypes.AGR_RESET)
    // The Drafts call is how CliqSign says whether this account has a plan; any other failure there
    // must not hide the saved list.
    const [setup, drafts] = await Promise.allSettled([
      EsignAgreementsApi.fetchEsignSetup(formId, variantId),
      EsignAgreementsApi.fetchEsignDrafts()
    ])

    if (setup.status === 'rejected') {
      commit(mutationTypes.AGR_FETCH_AGREEMENTS_FAILED)
      return
    }

    commit(mutationTypes.AGR_FETCH_AGREEMENTS_SUCCESS, {
      ...setup.value.data.data,
      loadedFor: key,
      esignAvailable: !(drafts.status === 'rejected' && isPlanError(drafts.reason))
    })
  },
  // One form version's agreement setup for the Submissions list (Signer 1's questions give each
  // row's name and email). Returned, not kept in state, so the After Submit editor's state is untouched.
  async fetchSetupForVariant(_, { formId, variantId }) {
    const response = await EsignAgreementsApi.fetchEsignSetup(formId, variantId)
    return response.data.data
  },
  // A Submission's agreements are shown in its own row only, so they are returned, not kept in state.
  async fetchLeadAgreements(_, { formId, leadId }) {
    const response = await EsignAgreementsApi.fetchLeadAgreements(formId, leadId)
    return response.data.data || []
  },
  async fetchLeadSendOptions(_, { formId, leadId }) {
    const response = await EsignAgreementsApi.fetchLeadSendOptions(formId, leadId)
    return response.data.data || { blocked_reason: null, agreements: [] }
  },
  async sendLeadAgreement(_, { formId, leadId, agreementId, values, message }) {
    const response = await EsignAgreementsApi.sendLeadAgreement(formId, leadId, { agreement_id: agreementId, values, message })
    return response.data.data
  },
  async fetchSignedPdf(_, { formId, leadId, recordId }) {
    const response = await EsignAgreementsApi.fetchSignedPdf(formId, leadId, recordId)
    return response.data.data
  },
  // The owner's CliqSign Drafts with their Fields, for the agreement editor. Not kept in state.
  async fetchDrafts() {
    const response = await EsignAgreementsApi.fetchEsignDrafts()
    return response.data.data || []
  },
  async saveAgreement({ dispatch }, { formId, variantId, id, agreement }) {
    await (id
      ? EsignAgreementsApi.updateEsignAgreement(formId, variantId, id, agreement)
      : EsignAgreementsApi.createEsignAgreement(formId, variantId, agreement))
    await dispatch('loadSetup', { formId, variantId, force: true })
  },
  async deleteAgreement({ dispatch }, { formId, variantId, id }) {
    await EsignAgreementsApi.deleteEsignAgreement(formId, variantId, id)
    await dispatch('loadSetup', { formId, variantId, force: true })
  },
  async setAgreementActive({ commit }, { formId, variantId, id, active }) {
    commit(mutationTypes.AGR_SET_AGREEMENT_ACTIVE, { id, active })
    try {
      await EsignAgreementsApi.setEsignAgreementActive(formId, variantId, id, active)
    } catch (error) {
      commit(mutationTypes.AGR_SET_AGREEMENT_ACTIVE, { id, active: !active })
      throw error
    }
  },
  async reorderAgreements({ commit, state }, { formId, variantId, agreements }) {
    const previous = state.agreements
    commit(mutationTypes.AGR_REORDER_AGREEMENTS, agreements)
    try {
      await EsignAgreementsApi.reorderEsignAgreements(formId, variantId, agreements.map(item => item.id))
    } catch (error) {
      commit(mutationTypes.AGR_REORDER_AGREEMENTS, previous)
      throw error
    }
  }
}

const mutations = {
  [mutationTypes.AGR_RESET](state) {
    Object.assign(state, emptyState())
  },
  [mutationTypes.AGR_FETCH_AGREEMENTS_SUCCESS](state, data) {
    state.agreements = data.agreements
    state.esignAvailable = data.esignAvailable
    state.loadedFor = data.loadedFor
    state.loaded = true
  },
  [mutationTypes.AGR_FETCH_AGREEMENTS_FAILED](state) {
    state.loadFailed = true
    state.loaded = true
  },
  [mutationTypes.AGR_SET_AGREEMENT_ACTIVE](state, { id, active }) {
    const agreement = state.agreements.find(item => item.id === id)
    if (agreement) {
      agreement.is_active = active
    }
  },
  [mutationTypes.AGR_REORDER_AGREEMENTS](state, agreements) {
    state.agreements = agreements
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
